<template>
<div class="x_box">
    <ol class="breadcrumb x_breadcrumb">
        <li class="breadcrumb-item">
           <router-link to="/sale/invoices">{{ $t('sale_invoice_list') }}</router-link>
        </li>
        <li class="breadcrumb-item active" aria-current="page">{{ $t('new') }}</li>
    </ol>
    <div class="x_header">
        <button type="button" class="btn btn-outline-primary btn-sm no-bor" @click="$router.go(-1)"><i class="fa fa-chevron-circle-left" aria-hidden="true"></i> {{ $t('back') }}</button>
        <b-btn variant="outline-primary no-bor"
               size="sm"
               @click="clickSaveHandler">
            <i class="fa" :class="canSave ? 'fa-floppy-o' : 'fa-exclamation-circle'" aria-hidden="true"/> {{canSave ? $t('save') : 'Fix highlighted fields'}}
        </b-btn>
    </div>
    <div class="x_body">
        <div class="card f_card">
            <div class="card-body">
                <div v-if="showValidation && validationErrors.length > 0" class="alert alert-warning invoice-alert">
                    <div class="invoice-alert-title">
                        <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
                        Please check the invoice before saving
                    </div>
                    <div v-for="(error, index) in validationErrors" :key="index" class="invoice-alert-line">
                        {{error}}
                    </div>
                </div>
                <div class="row">
                    <div class="col-sm-4 col-md-3">
                        <div class="form-group">
                            <label class="control-lable blue">{{ $t('customer') }}</label>
                            <div>
                                <CustomerDropdown @select="customerSelected" :value="invoice.CustomerName" />
                            </div>
                            <small v-if="invoice.CustomerId" class="text-muted">{{invoice.CustomerName}}</small>
                        </div>
                    </div>
                    <div class="col-sm-2 col-md-2">
                        <div class="form-group">
                            <label class="control-lable blue">{{ $t('date') }}</label>
                            <div>
                                <input type="date" class="form-control form-control-sm" v-model="date" @change="onDateChange" />
                            </div>
                        </div>
                    </div>
                    <div class="col-sm-2">
                        <div class="form-group">
                            <label class="control-lable blue">{{ $t('due_date') }}</label>
                            <div>
                                <input type="date" class="form-control form-control-sm" v-model="due_date" />
                            </div>
                        </div>
                    </div>
                </div>
                <hr>
                <div class="row">
                    <div class="col-sm-12">
                        <table class="grid">
                            <thead>
                                <tr>
                                    <th style="width: 30px;"></th>
                                    <th>{{$t('item_name')}}</th>
                                    <th style="width: 120px;">{{$t('uom')}}</th>
                                    <th style="width: 120px;">{{$t('qty_in_stock')}}</th>
                                    <th style="width: 120px;">{{$t('qty_sale')}}</th>
                                    <th style="width: 120px;">{{$t('cost')}}</th>
                                    <th style="width: 120px;">{{$t('price_sale')}}</th>
                                    <th style="width: 120px;">{{$t('discount')}}(%/$)</th>
                                    <th class="text-right" style="width: 120px;">{{$t('sub_total')}}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(ret,index) in lines" :key="ret.RowKey || ret.Id" :class="{'line-warning': lineHasWarning(ret)}">
                                    <td style="width: 30px;">
                                            <button type="button"
                                                    class="btn btn-danger btn-remove"
                                                    title="Remove line"
                                                    :disabled="ret.IsPromotionLine"
                                                    @click="removeLine(index)">
                                                <i class="fa fa-trash" aria-hidden="true"></i>
                                            </button>
                                        </td>
                                    <td>
                                        <AuthCompleted @select="select($event, ret)" :value="ret.ItemName" :disabled="ret.IsPromotionLine"/>
                                    </td>
                                    <td>
                                        <span v-if="ret.IsPromotionLine">{{ret.UomName}}</span>
                                        <UomDropdown v-else :value="ret.UomName" :item_id="ret.ItemId" @select="uomChange($event, ret)"></UomDropdown>
                                    </td>
                                    <td class="text-center">
                                        <input type="number" class="form-control form-control-sm" v-model="ret.QtyInStock" readonly />
                                    </td>
                                    <td class="text-center" :class="{'form-group-error': !ret.IsPromotionLine && ret.Quantity>ret.QtyInStock}">
                                        <input type="number" class="form-control form-control-sm" v-model.number="ret.Quantity"
                                               :disabled="ret.ItemId=='' || ret.IsPromotionLine"
                                               @keypress="$isKeyNumber"
                                               @input="scheduleReCalculate"
                                               @change="reCalculate">
                                    </td>
                                    <td class="text-center">
                                        <input type="number" class="form-control form-control-sm" v-model="ret.Cost" readonly />
                                    </td>
                                    <td class="text-right" :class="{'form-group-error': !ret.IsPromotionLine && !ret.PromotionText && ret.Price<=ret.Cost}">
                                        <div class="input-group">
                                            <input type="number"
                                                   class="form-control form-control-sm"
                                                   aria-describedby="price-info-addon"
                                                   v-model.number="ret.Price"
                                                   :disabled="ret.ItemId=='' || ret.IsPromotionLine"
                                                   @keypress="$isKeyNumber"
                                                   @input="onPriceInput(ret)"
                                                   @change="reCalculate">
                                            <div class="input-group-append">
                                                <span class="input-group-text price-info-addon" :class="{'has-promotion': hasPromotionInfo(ret)}" id="price-info-addon" @click="openPromotionInfo(ret)">
                                                    <i :class="priceInfoIcon(ret)" aria-hidden="true"></i>
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="text-right">
                                        <div class="input-group">
                                            <input type="number"
                                                   class="form-control form-control-sm"
                                                   aria-describedby="dis-addon"
                                                   v-model.number="ret.Discount"
                                                   :disabled="ret.ItemId=='' || ret.IsPromotionLine"
                                                   @keypress="$isKeyNumber"
                                                   @input="scheduleReCalculate"
                                                   @change="reCalculate">
                                            <div class="input-group-append">
                                                <span class="input-group-text" id="dis-addon" style="padding: 3px; width:30px; justify-content:center; cursor: pointer;" @click="onChangeDis(ret)">{{ret.DiscountType===1?'$':'%'}}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="text-right">
                                        <input type="text"
                                               class="form-control form-control-sm text-right"
                                               :value="$money(ret.SubTotal)" readonly />
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr>
                                    <th></th>
                                    <th rowspan="3" class="v-top">
                                        <button type="button"
                                                class="btn btn-outline-primary btn-sm no-bor"
                                                @click="addLine">
                                            <i class="fa fa-plus"/> {{ $t('add_line') }}
                                        </button>
                                    </th>
                                    <th colspan="6" class="text-right">{{$t('grand_total')}}:</th>
                                    <th class="text-right">{{$money(invoice.GrandTotal)}}</th>
                                </tr>
                                <tr>
                                    <th></th>
                                    <th colspan="6" class="text-right">{{$t('discount')}}:</th>
                                    <th class="text-right">{{$money(invoice.Discount)}}</th>
                                </tr>
                                <tr>
                                    <th></th>
                                    <th colspan="6" class="text-right">{{$t('total_amount')}}:</th>
                                    <th class="text-right">{{$money(invoice.TotalAmount)}}</th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
                <br>
                <hr>
                <div class="row">
                    <div class="col-sm-6">
                        <div class="form-group">
                            <label class="control-lable blue">{{ $t('noted') }}</label>
                            <div>
                                <textarea class="form-control form-control-sm" v-model="invoice.Description"> </textarea>
                            </div>
                        </div>
                    </div>
                    <div class="col-sm-6">
                        <div class="invoice-summary">
                            <div class="invoice-summary-row">
                                <span>{{ $t('grand_total') }}</span>
                                <strong>{{$money(invoice.GrandTotal || 0)}}</strong>
                            </div>
                            <div class="invoice-summary-row">
                                <span>{{ $t('discount') }}</span>
                                <strong>{{$money(invoice.Discount || 0)}}</strong>
                            </div>
                            <div class="invoice-summary-row total">
                                <span>{{ $t('total_amount') }}</span>
                                <strong>{{$money(invoice.TotalAmount || 0)}}</strong>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <b-modal ref="promotionInfoModal" size="lg" hide-footer modal-class="promotion-info-modal">
        <template #modal-title>
            <div class="promotion-modal-title">
                <i class="fa fa-info-circle" aria-hidden="true"></i>
                <span>Promotion price detail</span>
            </div>
        </template>
        <div v-if="selectedPromotionLine" class="promotion-info-content">
            <div class="promotion-hero">
                <div>
                    <h5>{{selectedPromotionLine.ItemName || '-'}}</h5>
                    <p>{{selectedPromotionLine.PromotionText || selectedPromotionLine.PromotionStatusText || promotionStatusText(selectedPromotionLine)}}</p>
                </div>
            </div>
            <div class="promotion-summary">
                <div>
                    <span>Qty</span>
                    <strong>{{selectedPromotionLine.Quantity || 0}}</strong>
                </div>
                <div>
                    <span>Base price</span>
                    <strong>{{$money(selectedPromotionLine.BasePrice || 0)}}</strong>
                </div>
                <div>
                    <span>Current price</span>
                    <strong>{{$money(selectedPromotionLine.Price || 0)}}</strong>
                </div>
                <div>
                    <span>Saving</span>
                    <strong :class="promotionAdjustmentClass(selectedPromotionLine)">{{promotionAdjustmentText(selectedPromotionLine)}}</strong>
                </div>
            </div>
            <div v-if="activePromotionRules(selectedPromotionLine).length === 0" class="promotion-empty">
                <div class="promotion-empty-icon">
                    <i class="fa fa-tags" aria-hidden="true"></i>
                </div>
                <strong>No active promotion</strong>
                <span>This item is using normal sale price for the selected invoice date and quantity.</span>
            </div>
            <div v-for="(rule, ruleIndex) in activePromotionRules(selectedPromotionLine)" :key="rule.Id || ruleIndex" class="promotion-rule">
                <div class="promotion-rule-title">
                    <strong>{{promotionTypeName(rule)}}</strong>
                    <span>{{promotionDateRange(rule)}}</span>
                </div>
                <table class="table table-sm promotion-table">
                    <thead>
                        <tr v-if="promotionType(rule) === 1">
                            <th>Min qty</th>
                            <th>Max qty</th>
                            <th class="text-right">Promotion price</th>
                            <th class="text-right">Status</th>
                        </tr>
                        <tr v-else-if="promotionType(rule) === 2">
                            <th>Buy from</th>
                            <th>Free item</th>
                            <th class="text-right">Status</th>
                        </tr>
                        <tr v-else>
                            <th>Item</th>
                            <th class="text-right">Special price</th>
                            <th class="text-right">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(ret, index) in getRuleLines(rule)" :key="index" :class="{'promotion-match': isPromotionLineMatched(selectedPromotionLine, rule, ret)}">
                            <template v-if="promotionType(rule) === 1">
                                <td>{{promotionField(ret, 'MinQty') || 0}}</td>
                                <td>{{promotionMaxQtyText(ret)}}</td>
                                <td class="text-right">{{$money(promotionField(ret, 'Price') || 0)}}</td>
                                <td class="text-right"><span :class="promotionStatusClass(selectedPromotionLine, rule, ret)">{{promotionStatusLabel(selectedPromotionLine, rule, ret)}}</span></td>
                            </template>
                            <template v-else-if="promotionType(rule) === 2">
                                <td>{{promotionField(ret, 'MinQty') || 0}}</td>
                                <td>{{promotionField(ret, 'ItemName') || '-'}}</td>
                                <td class="text-right"><span :class="promotionStatusClass(selectedPromotionLine, rule, ret)">{{promotionStatusLabel(selectedPromotionLine, rule, ret)}}</span></td>
                            </template>
                            <template v-else>
                                <td>{{promotionField(ret, 'ItemName') || '-'}}</td>
                                <td class="text-right">{{$money(promotionField(ret, 'Price') || 0)}}</td>
                                <td class="text-right"><span :class="promotionStatusClass(selectedPromotionLine, rule, ret)">{{promotionStatusLabel(selectedPromotionLine, rule, ret)}}</span></td>
                            </template>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </b-modal>
</div>
</template>

<script>
    import { AuthCompleted } from '../Item';
    import { CustomerDropdown } from '../Customer';
    import UomDropdown from '../UOM/Dropdown';
    import moment from 'moment';
    export default {
        components: {
            AuthCompleted,
            CustomerDropdown,
            UomDropdown
        },
        data(){
            return {
                invoice: {
                    CustomerId: '',
                    CustomerName: '',
                    CustomerTypeId: '',
                    GrandTotal: 0,
                    UntaxAmount: 0,
                    ExchangeRate: 0,
                    Description: '',
                },
                lines: [this.newLine()],
                date: this.$now().format('YYYY-MM-DD'),
                due_date: this.$now().format('YYYY-MM-DD'),
                actionLink: 'sale/invoice/save',
                types: [],
                showValidation: false,
                promotionItemCache: {},
                reCalculateTimer: null,
                selectedPromotionLine: null
            }
        },
        async created(){
            let id = this.$route.params.id;
            if(id != undefined && id != null && id != ''){
                var loading = this.$loading.show();
                this.actionLink = 'sale/invoice/update';
                this.$api().post('sale/invoice/find', { Id: id }).then(res => {
                    var data = res.data.Data;
                    this.invoice = data.SaleInvoice;
                    this.lines = data.SaleInvoiceLines.map(line => ({
                        ...this.newLine(),
                        ...line,
                        BasePrice: line.Price
                    }));
                    this.date = this.$format(this.invoice.Date, 'YYYY-MM-DD');
                    this.due_date = this.$format(this.invoice.DueDate, 'YYYY-MM-DD');
                }).finally(function() { loading.hide(); });
            }
            await this.typeList();
        },
        methods: {
            async typeList(){
                var res = await this.$api().post('customer/types');
                if(this.$isValid(res)){
                    this.types = res.data.Json.Types;
                }
            },
            async clickSaveHandler(){
                this.showValidation = true;
                if(!this.canSave) return;
                await this.reCalculate();
                this.invoice.Date = this.date;
                this.invoice.DueDate = this.due_date;
                var req = {
                    Header: this.invoice,
                    Lines: this.lines.filter(t => t.ItemId !== '').map(this.cleanLineForSave)
                };
                var loading = this.$loading.show();
                this.$api().post(this.actionLink, req).then(res => {
                    if(this.$isValid(res)){this.$router.push('/sale/invoices');}
                }).finally(function() { loading.hide(); });
            },
            async select(item, line){
                line.ItemId = item.Id;
                line.ItemName = item.Name;
                const defaultUom = await this.getDefaultItemUom(item);
                line.Price = defaultUom.Price;
                line.BasePrice = defaultUom.Price;
                line.IsManualPrice = false;
                line.UomId = defaultUom.UomId;
                line.UomName = defaultUom.UomName;
                line.Cost = defaultUom.Cost;
                line.QtyInStock = item.EndingQty;
                line.PriceRules = await this.getItemPromotions(item.Id);
                await this.reCalculate();
            },
            customerSelected(item){
                this.invoice.CustomerId = item.Id;
                this.invoice.CustomerName = item.Name;
                this.invoice.CustomerTypeId = item.CustomerTypeId;
                this.updateDueDate();
            },
            scheduleReCalculate(){
                clearTimeout(this.reCalculateTimer);
                this.reCalculateTimer = setTimeout(() => {
                    this.reCalculate();
                }, 150);
            },
            async reCalculate(){
                clearTimeout(this.reCalculateTimer);
                await this.applyPromotions();
                this.calculateTotals();
            },
            calculateTotals(){
                let grand = 0;
                let totalDis = 0;
                let untax = 0;
                let totalVat = 0;
                let totalAmount = 0;
                this.lines.forEach(item => {
                    let discount = item.DiscountType == 1? item.Discount: item.Price * item.Discount / 100;
                    let newPrice = item.Price - discount;
                    let total = item.Quantity * newPrice;
                    let taxVat = total * (item.TaxLine / 100);
                    let subTotal = total + taxVat;
                    item.SubTotal = subTotal;
                    totalDis += discount * item.Quantity;
                    untax += total;
                    totalVat += taxVat;
                    grand += item.Quantity * item.Price;
                    totalAmount += subTotal;
                });
                this.invoice.GrandTotal = grand;
                this.invoice.Discount = totalDis;
                this.invoice.UntaxAmount = untax;
                this.invoice.TaxVat = totalVat;
                this.invoice.TotalAmount = totalAmount;
                this.invoice.Balance = totalAmount;
            },
            newRowKey(){
                return `row-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`;
            },
            newLine(){
                return {
                    RowKey: this.newRowKey(),
                    ItemId: '',
                    ItemName: '',
                    QtyInStock: 0,
                    Quantity: 1,
                    DiscountAmount: 0,
                    DiscountType: 1,
                    UomId: '',
                    UomName: '',
                    TaxLine: 0,
                    Discount: 0,
                    Price: 0,
                    BasePrice: 0,
                    Cost: 0,
                    SubTotal: 0,
                    ExchangeRate: 0,
                    PriceRules: [],
                    PromotionText: '',
                    PromotionStatusText: '',
                    IsPromotionLine: false,
                    IsManualPrice: false,
                    PromotionParentItemId: ''
                };
            },
            addLine(){
                this.lines.push(this.newLine());
            },
            removeLine(index){
                if(this.lines.length === 1){
                    this.lines.splice(index, 1, this.newLine());
                }else{
                    this.lines.splice(index, 1);
                }
                this.reCalculate();
            },
            uomChange(e, line){
                line.UomId = e.UomId;
                line.UomName = e.UomName;
                line.Price = e.Price;
                line.BasePrice = e.Price;
                line.IsManualPrice = false;
                line.Cost = e.Cost;
                this.reCalculate();
            },
            onPriceInput(line){
                line.IsManualPrice = true;
                this.scheduleReCalculate();
            },
            onDateChange(){
                this.updateDueDate();
                this.reCalculate();
            },
            onChangeDis(line){
                if(line.DiscountType === 1) line.DiscountType = 2;
                else line.DiscountType = 1;
                if(line.ItemId !== '') this.reCalculate();
            },
            lineHasWarning(line){
                if(line.IsPromotionLine) return false;
                if(line.ItemId === '') return true;
                if(Number(line.Quantity) <= 0) return true;
                if(Number(line.Quantity) > Number(line.QtyInStock)) return true;
                if(line.PromotionText) return false;
                return Number(line.Price) <= Number(line.Cost);
            },
            async openPromotionInfo(line){
                if(line.ItemId && (!line.PriceRules || line.PriceRules.length === 0)){
                    line.PriceRules = await this.getItemPromotions(line.ItemId);
                    await this.reCalculate();
                }
                this.selectedPromotionLine = line;
                this.$refs.promotionInfoModal.show();
            },
            hasPromotionInfo(line){
                return !!(line && (line.PromotionText || this.activePromotionRules(line).length > 0));
            },
            priceInfoIcon(line){
                return this.hasPromotionInfo(line) ? 'fa fa-gift' : 'fa fa-info-circle';
            },
            async getItemPromotions(itemId){
                if(!itemId) return [];
                if(this.promotionItemCache[itemId] && this.promotionItemCache[itemId].PriceRules){
                    return this.promotionItemCache[itemId].PriceRules;
                }
                const res = await this.$api().post('item/find', { Id: itemId });
                if(this.$isValid(res)){
                    const rules = res.data.Json.Promotions || [];
                    this.promotionItemCache[itemId] = {
                        Item: res.data.Data,
                        PriceRules: rules
                    };
                    return rules;
                }
                return [];
            },
            async getPromotionItem(itemId){
                if(!itemId) return null;
                if(this.promotionItemCache[itemId] && this.promotionItemCache[itemId].Item){
                    return this.promotionItemCache[itemId].Item;
                }
                const res = await this.$api().post('item/find', { Id: itemId });
                if(this.$isValid(res)){
                    this.promotionItemCache[itemId] = {
                        ...(this.promotionItemCache[itemId] || {}),
                        Item: res.data.Data,
                        PriceRules: res.data.Json.Promotions || []
                    };
                    return res.data.Data;
                }
                return null;
            },
            async getPromotionItemUom(itemId, item){
                if(this.promotionItemCache[itemId] && this.promotionItemCache[itemId].Uom){
                    return this.promotionItemCache[itemId].Uom;
                }
                const uoms = await this.getItemUoms(itemId);
                const uom = this.findDefaultUom(uoms, item);
                this.promotionItemCache[itemId] = {
                    ...(this.promotionItemCache[itemId] || {}),
                    Uom: uom
                };
                return uom;
            },
            async getDefaultItemUom(item){
                const uoms = await this.getItemUoms(item.Id);
                return this.findDefaultUom(uoms, item);
            },
            async getItemUoms(itemId){
                if(!itemId) return [];
                if(this.promotionItemCache[itemId] && this.promotionItemCache[itemId].ItemUoms){
                    return this.promotionItemCache[itemId].ItemUoms;
                }
                const res = await this.$api().post('item/uoms', { Id: itemId });
                if(this.$isValid(res)){
                    const uoms = res.data.Json.ItemPrices || [];
                    this.promotionItemCache[itemId] = {
                        ...(this.promotionItemCache[itemId] || {}),
                        ItemUoms: uoms
                    };
                    return uoms;
                }
                return [];
            },
            findDefaultUom(uoms, item){
                const defaultUom = (uoms || []).find(t => String(t.UomId || '').toLowerCase() === String(item.UomId || '').toLowerCase());
                const hasUomPrice = defaultUom && defaultUom.Price !== null && defaultUom.Price !== undefined;
                const hasUomCost = defaultUom && defaultUom.Cost !== null && defaultUom.Cost !== undefined;
                return {
                    UomId: (defaultUom && defaultUom.UomId) || item.UomId,
                    UomName: (defaultUom && defaultUom.UomName) || item.UomName || '',
                    Price: Number(hasUomPrice ? defaultUom.Price : (item.Price || 0)),
                    Cost: Number(hasUomCost ? defaultUom.Cost : (item.Cost || 0))
                };
            },
            async applyPromotions(){
                const normalLines = this.lines.filter(t => !t.IsPromotionLine);
                this.lines = normalLines;
                normalLines.forEach(line => {
                    if(line.ItemId !== ''){
                        if(!line.IsManualPrice) line.Price = Number(line.BasePrice || line.Price || 0);
                        line.PromotionText = '';
                        line.PromotionStatusText = '';
                    }
                });

                for(const line of normalLines){
                    const allRules = line.PriceRules || [];
                    const rules = allRules.filter(rule => this.isPromotionActive(rule));
                    let isApplied = false;

                    for(const rule of rules){
                        const type = this.promotionType(rule);
                        if(type === 1) isApplied = this.applyQuantityPricePromotion(line, rule) || isApplied;
                        if(type === 2) isApplied = await this.applyFreeItemPromotion(line, rule) || isApplied;
                        if(type === 3) isApplied = this.applyBundlePricePromotion(line, rule, normalLines) || isApplied;
                    }

                    line.PromotionStatusText = this.getPromotionStatus(line, allRules, rules, isApplied);
                }
            },
            applyQuantityPricePromotion(line, rule){
                const qty = Number(line.Quantity || 0);
                const match = this.getRuleLines(rule)
                    .filter(ret => this.isQtyInPromotionRange(qty, ret))
                    .sort((a, b) => Number(this.promotionField(b, 'MinQty') || 0) - Number(this.promotionField(a, 'MinQty') || 0))[0];
                if(match){
                    if(line.IsManualPrice) return false;
                    line.Price = Number(this.promotionField(match, 'Price') || 0);
                    line.PromotionText = `Promotion price ${this.$money(line.Price)}`;
                    return true;
                }
                return false;
            },
            async applyFreeItemPromotion(line, rule){
                const qty = Number(line.Quantity || 0);
                let isApplied = false;
                for(const ret of this.getRuleLines(rule)){
                    const promotionItemId = this.promotionField(ret, 'ItemId');
                    if(qty < Number(this.promotionField(ret, 'MinQty') || 0) || !promotionItemId) continue;
                    const item = await this.getPromotionItem(promotionItemId);
                    if(!item) continue;
                    const uom = await this.getPromotionItemUom(promotionItemId, item);
                    this.lines.push({
                        ...this.newLine(),
                        ItemId: item.Id,
                        ItemName: item.Name,
                        UomId: item.UomId,
                        UomName: this.promotionField(ret, 'UomName') || uom.UomName || '',
                        QtyInStock: item.EndingQty,
                        Quantity: 1,
                        Cost: uom.Cost || item.Cost,
                        Price: 0,
                        BasePrice: 0,
                        SubTotal: 0,
                        IsPromotionLine: true,
                        PromotionParentItemId: line.ItemId,
                        PromotionText: `Free item from ${line.ItemName}`
                    });
                    isApplied = true;
                }
                return isApplied;
            },
            applyBundlePricePromotion(line, rule, normalLines){
                const match = this.getRuleLines(rule).find(ret => {
                    const itemId = this.promotionField(ret, 'ItemId');
                    return itemId && normalLines.some(t => t.ItemId === itemId);
                });
                if(match){
                    if(line.IsManualPrice) return false;
                    line.Price = Number(this.promotionField(match, 'Price') || 0);
                    line.PromotionText = `Bundle price ${this.$money(line.Price)}`;
                    return true;
                }
                return false;
            },
            getPromotionStatus(line, allRules, activeRules, isApplied){
                if(!line.ItemId) return '';
                if(!allRules || allRules.length === 0) return 'No promotion is set for this item.';
                if(!activeRules || activeRules.length === 0) return 'Promotion exists, but it is not active for the selected invoice date.';
                if(!isApplied) return `Promotion exists, but quantity ${line.Quantity || 0} does not match the condition.`;
                return line.PromotionText || 'Promotion applied.';
            },
            activePromotionRules(line){
                if(!line) return [];
                return (line.PriceRules || []).filter(rule => this.isPromotionActive(rule));
            },
            promotionTypeName(rule){
                const type = this.promotionType(rule);
                if(type === 1) return 'Quantity price';
                if(type === 2) return 'Free item';
                if(type === 3) return 'Special price';
                return 'Promotion';
            },
            promotionDateRange(rule){
                const startDate = this.toDateKey(rule.StartDate) || '-';
                const endDate = this.toDateKey(rule.EndDate) || '-';
                return `${startDate} - ${endDate}`;
            },
            promotionStatusText(line){
                if(!line || !line.ItemId) return 'Select an item to see promotion detail.';
                if(line.PromotionStatusText) return line.PromotionStatusText;
                if(this.activePromotionRules(line).length === 0) return 'No active promotion for the selected invoice date.';
                return 'Promotion rules are available. Matched rows are highlighted.';
            },
            promotionAdjustmentText(line){
                const basePrice = Number(line.BasePrice || 0);
                const currentPrice = Number(line.Price || 0);
                const qty = Number(line.Quantity || 0);
                const diff = (basePrice - currentPrice) * qty;
                if(diff > 0) return this.$money(diff);
                if(diff < 0) return `+ ${this.$money(Math.abs(diff))}`;
                return this.$money(0);
            },
            promotionAdjustmentClass(line){
                const diff = (Number(line.BasePrice || 0) - Number(line.Price || 0)) * Number(line.Quantity || 0);
                if(diff > 0) return 'is-saving';
                if(diff < 0) return 'is-increase';
                return '';
            },
            promotionMaxQtyText(ret){
                const rawMaxQty = this.promotionField(ret, 'MaxQty');
                const maxQty = Number(rawMaxQty);
                if(rawMaxQty === null || rawMaxQty === undefined || rawMaxQty === '' || maxQty <= 0) return 'Up';
                return rawMaxQty;
            },
            isPromotionLineMatched(line, rule, ret){
                const type = this.promotionType(rule);
                if(type === 1) return this.isQtyInPromotionRange(Number(line.Quantity || 0), ret);
                if(type === 2) return Number(line.Quantity || 0) >= Number(this.promotionField(ret, 'MinQty') || 0);
                if(type === 3) {
                    const itemId = this.promotionField(ret, 'ItemId');
                    return !!itemId && this.lines.some(t => !t.IsPromotionLine && t.ItemId === itemId);
                }
                return false;
            },
            promotionStatusLabel(line, rule, ret){
                return this.isPromotionLineMatched(line, rule, ret) ? 'Applied' : 'Not matched';
            },
            promotionStatusClass(line, rule, ret){
                return this.isPromotionLineMatched(line, rule, ret) ? 'status-pill applied' : 'status-pill';
            },
            getRuleLines(rule){
                if(Array.isArray(rule.JsonObject)) return rule.JsonObject;
                if(typeof rule.JsonObject === 'string'){
                    try {
                        const parsed = JSON.parse(rule.JsonObject);
                        return Array.isArray(parsed) ? parsed : [];
                    } catch (e) {
                        return [];
                    }
                }
                return [];
            },
            isQtyInPromotionRange(qty, ret){
                const minQty = Number(this.promotionField(ret, 'MinQty') || 0);
                const rawMaxQty = this.promotionField(ret, 'MaxQty');
                const maxQty = Number(rawMaxQty);
                const hasMaxQty = rawMaxQty !== null && rawMaxQty !== undefined && rawMaxQty !== '' && maxQty > 0;
                return qty >= minQty && (!hasMaxQty || qty <= maxQty);
            },
            promotionField(ret, field){
                if(!ret) return undefined;
                if(ret[field] !== undefined) return ret[field];
                const camelField = field.charAt(0).toLowerCase() + field.slice(1);
                if(ret[camelField] !== undefined) return ret[camelField];
                const snakeField = field.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`).replace(/^_/, '');
                if(ret[snakeField] !== undefined) return ret[snakeField];
                return undefined;
            },
            isPromotionActive(rule){
                if(rule.IsActive === false) return false;
                const invoiceDate = this.toDateKey(this.date);
                if(rule.StartDate && invoiceDate < this.toDateKey(rule.StartDate)) return false;
                if(rule.EndDate && invoiceDate > this.toDateKey(rule.EndDate)) return false;
                return true;
            },
            toDateKey(value){
                return String(value || '').substr(0, 10);
            },
            promotionType(rule){
                const type = Number(rule.RuleType);
                if(!isNaN(type)) return type;
                const text = String(rule.RuleType || '').toUpperCase();
                if(text.indexOf('QUANTITY') >= 0) return 1;
                if(text.indexOf('FREE') >= 0 || text.indexOf('GET') >= 0) return 2;
                if(text.indexOf('BUNDLE') >= 0 || text.indexOf('SPECIAL') >= 0) return 3;
                return 0;
            },
            cleanLineForSave(line){
                const clean = { ...line };
                delete clean.BasePrice;
                delete clean.PriceRules;
                delete clean.PromotionText;
                delete clean.PromotionStatusText;
                delete clean.IsPromotionLine;
                delete clean.IsManualPrice;
                delete clean.PromotionParentItemId;
                delete clean.RowKey;
                return clean;
            },
            updateDueDate(){
                const ctype = this.types.find(el => el.Id === this.invoice.CustomerTypeId);
                if(ctype !== undefined){
                    this.due_date = moment(this.date).add(ctype.DueDay, 'days').format('YYYY-MM-DD');
                }else{
                    if(moment(this.date).diff(this.due_date, 'days') > 0) this.due_date = this.date;
                }
            }
        },
        computed: {
            permission(){
                return this.$store.state.userGroup;
            },
            lineCount(){
                return this.lines.filter(t => t.ItemId !== '').length;
            },
            validationErrors(){
                let errors = [];
                if(this.invoice.CustomerId === '') errors.push('Select a customer.');
                if(this.lineCount === 0) errors.push('Add at least one item.');
                let badQuantity = this.lines.some(t => t.ItemId !== '' && Number(t.Quantity) <= 0);
                if(badQuantity) errors.push('Quantity must be greater than zero.');
                let lowStock = this.lines.some(t => t.ItemId !== '' && !t.IsPromotionLine && Number(t.Quantity) > Number(t.QtyInStock));
                if(lowStock) errors.push('Some lines are above available stock.');
                let badPrice = this.lines.some(t => t.ItemId !== '' && !t.IsPromotionLine && !t.PromotionText && Number(t.Price) <= Number(t.Cost));
                if(badPrice) errors.push('Some sale prices are not above cost.');
                return errors;
            },
            canSave(){
                return this.validationErrors.length === 0;
            }
        },
        beforeDestroy(){
            clearTimeout(this.reCalculateTimer);
        }
    }
</script>
<style scoped>
    label{
        margin-bottom: 0;
    }
    .invoice-alert{
        border-left: 4px solid #f0ad4e;
        padding: 10px 12px;
    }
    .invoice-alert-title{
        font-weight: 700;
        margin-bottom: 4px;
    }
    .invoice-alert-line{
        font-size: 13px;
    }
    .line-warning{
        background: #fffaf2;
    }
    .invoice-summary{
        border-top: 1px solid #ddd;
        margin-top: 18px;
        padding-top: 8px;
    }
    .invoice-summary-row{
        display: flex;
        justify-content: space-between;
        padding: 4px 0;
    }
    .invoice-summary-row.total{
        border-top: 1px solid #ddd;
        color: #0856ab;
        font-size: 16px;
        margin-top: 4px;
        padding-top: 8px;
    }
    .price-info-addon{
        color: #0856ab;
        cursor: pointer;
        justify-content: center;
        padding: 3px;
        width: 30px;
    }
    .price-info-addon.has-promotion{
        background: #eaf7ef;
        border-color: #bfe5c9;
        color: #1f7a3a;
    }
    .promotion-modal-title{
        align-items: center;
        color: #0856ab;
        display: flex;
        font-weight: 700;
        gap: 8px;
    }
    .promotion-info-content{
        color: #2f353a;
    }
    .promotion-info-content /deep/ .modal-content,
    .promotion-info-modal /deep/ .modal-content{
        border-radius: 3px;
    }
    .promotion-hero{
        background: linear-gradient(135deg, #f4f9ff 0%, #ffffff 72%);
        border: 1px solid #d8e7f8;
        border-radius: 3px;
        margin-bottom: 10px;
        padding: 12px 14px;
    }
    .promotion-hero h5{
        color: #0856ab;
        font-size: 16px;
        font-weight: 700;
        line-height: 1.35;
        margin: 0;
    }
    .promotion-hero p{
        color: #5f6f82;
        font-size: 13px;
        margin: 4px 0 0;
    }
    .promotion-summary{
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 8px;
        margin-bottom: 12px;
    }
    .promotion-summary div{
        background: #f8fbff;
        border: 1px solid #dbe8f7;
        border-radius: 3px;
        padding: 8px 10px;
    }
    .promotion-summary span{
        color: #6c757d;
        display: block;
        font-size: 11px;
    }
    .promotion-summary strong{
        color: #0856ab;
        display: block;
        font-size: 14px;
        margin-top: 2px;
    }
    .promotion-summary strong.is-saving{
        color: #1f7a3a;
    }
    .promotion-summary strong.is-increase{
        color: #9f3030;
    }
    .promotion-empty{
        align-items: center;
        color: #6c757d;
        display: flex;
        flex-direction: column;
        padding: 22px 16px;
        text-align: center;
    }
    .promotion-empty-icon{
        align-items: center;
        background: #eef5fc;
        border-radius: 50%;
        color: #0856ab;
        display: flex;
        height: 42px;
        justify-content: center;
        margin-bottom: 8px;
        width: 42px;
    }
    .promotion-empty strong{
        color: #34495e;
        font-size: 15px;
        margin-bottom: 4px;
    }
    .promotion-empty span{
        max-width: 420px;
    }
    .promotion-rule{
        border: 1px solid #dbe8f7;
        border-radius: 3px;
        margin-top: 10px;
        overflow: hidden;
    }
    .promotion-rule-title{
        align-items: center;
        background: #f8fbff;
        color: #0856ab;
        display: flex;
        justify-content: space-between;
        padding: 9px 10px;
    }
    .promotion-rule-title span{
        color: #6c757d;
        font-size: 12px;
    }
    .promotion-table{
        margin-bottom: 0;
    }
    .promotion-table thead th{
        background: #fff;
        border-bottom: 1px solid #dbe8f7;
        color: #526579;
        font-size: 12px;
        font-weight: 700;
        padding: 8px 10px;
        text-transform: uppercase;
    }
    .promotion-table tbody td{
        border-color: #eef3f8;
        padding: 8px 10px;
        vertical-align: middle;
    }
    .promotion-match{
        background: #ecf8f0;
        color: #1f7a3a;
        font-weight: 600;
    }
    .status-pill{
        background: #f0f3f5;
        border-radius: 999px;
        color: #6c757d;
        display: inline-block;
        font-size: 11px;
        font-weight: 700;
        min-width: 86px;
        padding: 3px 8px;
        text-align: center;
    }
    .status-pill.applied{
        background: #dff3e5;
        color: #1f7a3a;
    }
</style>
